
import { defineComponent } from 'vue';
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonButton, IonIcon, IonSpinner, alertController } from '@ionic/vue';
import {
  arrowBackOutline, refreshOutline, callOutline, chatbubbleEllipsesOutline, calendarOutline, trendingUpOutline,
  checkmarkDoneOutline, swapHorizontalOutline, createOutline, closeCircleOutline,
} from 'ionicons/icons';
import FormSheet from './FormSheet.vue';
import UserPicker from './UserPicker.vue';
import { periodWord } from '../../deals';
import {
  leadApi, leadApiForm, loadMeta, currentPosition, STAGE_COLORS, optionColor, optionDefault, capturesLocation, needsNote, stageLabel, formatDateTime, formatMoney, isPast,
} from '../../salesLeads';

export default defineComponent({
  name: 'SalesLeadDetail',
  components: { IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonButton, IonIcon, IonSpinner, FormSheet, UserPicker },
  data() {
    return {
      lead: null as any,
      permissions: {} as any,
      meta: null as any,
      tab: 'meetings',
      sheet: null as string | null,
      busy: false,
      file: null as File | null,
      stageForm: { stage: '', note: '' },
      activityForm: { type: 'Call', outcome: '', notes: '', next_follow_up_at: '', withLocation: false },
      meetingForm: { meeting_type: 'Intro', mode: 'In-person', scheduled_at: '', location: '', attendees_client: '', attendees_internal: [] as number[], agenda: '' },
      transferForm: { to_user_id: null as number | null, reason: '', note: '' },
      closeForm: { close_as: 'Lost', reason: '', note: '' },
      arrowBackOutline, refreshOutline, callOutline, chatbubbleEllipsesOutline, calendarOutline, trendingUpOutline,
      checkmarkDoneOutline, swapHorizontalOutline, createOutline, closeCircleOutline,
    };
  },
  computed: {
    leadId(): string {
      return this.$route.params.leadId as string;
    },
    openStages(): string[] {
      return this.meta ? this.meta.open_stages : [];
    },
    tabs(): { key: string; label: string }[] {
      if (!this.lead) {
        return [];
      }
      return [
        { key: 'meetings', label: `Meetings (${this.lead.meetings.length})` },
        { key: 'activities', label: `Activities (${this.lead.activities.length})` },
        { key: 'history', label: 'History' },
        { key: 'details', label: 'Details' },
      ];
    },
    unitLabel(): string {
      return this.lead && this.lead.product ? this.lead.product.unit_label : 'Ship';
    },
    detailRows(): Array<Array<any>> {
      const l = this.lead;
      return [
        ['Phone', l.contact_phone], ['Email', l.contact_email], ['Address', l.address],
        ['Port', l.port ? l.port.port_name : null],
        ['Existing client', l.client ? 'Yes — ' + l.client.client_name : 'No'],
        ['Existing company', l.company ? 'Yes — ' + l.company.company_name : 'No (new client)'],
        ['Source', l.source + (l.source_detail ? ' · ' + l.source_detail : '')], ['Segment', l.segment],
        ['Product', l.product ? l.product.name : null],
        ['Expected close', l.expected_close_date], ['Created', formatDateTime(l.created_at) + (l.created_by ? ' by ' + l.created_by.name : '')],
        ['Remarks', l.remarks],
      ];
    },
  },
  watch: {
    '$route.params.leadId'(id: string) {
      if (id && this.$route.path.startsWith('/sales-leads/')) {
        this.load();
      }
    },
  },
  async created() {
    this.meta = await loadMeta().catch(() => null);
    this.resetActivity();
    this.resetMeeting();
    this.load();
  },
  methods: {
    label: stageLabel,
    format: formatDateTime,
    money: formatMoney,
    periodWord,
    past: isPast,
    needsNote,
    stageColor(stage: string) {
      return STAGE_COLORS[stage] || '#64748b';
    },
    priorityColor(p: string) {
      return optionColor(this.meta, 'priority', p);
    },
    goBack() {
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        this.$router.push('/sales-leads');
      }
    },
    transferText(t: any): string {
      const from = t.from_user ? t.from_user.name : '';
      const to = t.to_user ? t.to_user.name : '';
      return `${from} → ${to} (${t.reason}${t.note ? ': ' + t.note : ''})`;
    },
    load() {
      leadApi('get', { lead_id: this.leadId }, true).then(response => {
        this.lead = response.data.lead;
        this.permissions = response.data.permissions;
      }).catch(() => {
        this.$router.replace('/sales-leads');
      });
    },
    openSheet(name: string) {
      this.file = null;
      if (name === 'stage') {
        this.stageForm = { stage: this.lead.stage, note: '' };
      }
      this.sheet = name;
    },
    onFile(event: Event) {
      const files = (event.target as HTMLInputElement).files;
      this.file = files && files.length ? files[0] : null;
    },
    async done(message: string) {
      this.busy = false;
      this.sheet = null;
      const toast = await alertController.create({ header: 'Done', message, buttons: ['OK'] });
      await toast.present();
      this.load();
    },
    fail() {
      this.busy = false;
    },
    async warn(message: string) {
      const alert = await alertController.create({ header: 'Missing information', message, buttons: ['OK'] });
      await alert.present();
    },
    submitStage() {
      this.busy = true;
      leadApi('changeStage', { lead_id: this.leadId, ...this.stageForm }).then(r => this.done(r.data.message)).catch(this.fail);
    },
    async submitActivity() {
      if (!this.activityForm.notes.trim()) {
        return;
      }
      this.busy = true;
      const { withLocation, ...form } = this.activityForm;
      const pos = withLocation || capturesLocation(this.meta, form.type) ? await currentPosition() : { lat: null, lng: null };
      leadApiForm('logActivity', { lead_id: this.leadId, ...form, lat: pos.lat, lng: pos.lng }, this.file)
        .then(r => {
          this.resetActivity();
          this.done(r.data.message);
        }).catch(this.fail);
    },
    submitMeeting() {
      if (!this.meetingForm.scheduled_at) {
        return;
      }
      this.busy = true;
      leadApi('scheduleMeeting', { lead_id: this.leadId, ...this.meetingForm }).then(r => {
        this.resetMeeting();
        this.done(r.data.message);
      }).catch(this.fail);
    },
    resetActivity() {
      this.activityForm = { type: optionDefault(this.meta, 'activity_type'), outcome: '', notes: '', next_follow_up_at: '', withLocation: false };
    },
    resetMeeting() {
      this.meetingForm = {
        meeting_type: optionDefault(this.meta, 'meeting_type'), mode: optionDefault(this.meta, 'meeting_mode'),
        scheduled_at: '', location: '', attendees_client: '', attendees_internal: [], agenda: '',
      };
    },
    submitTransfer() {
      if (!this.transferForm.to_user_id || !this.transferForm.reason) {
        return;
      }
      if (needsNote(this.meta, 'transfer_reason', this.transferForm.reason) && !this.transferForm.note.trim()) {
        this.warn('Add a note for this reason.');
        return;
      }
      this.busy = true;
      leadApi('requestTransfer', { lead_id: this.leadId, ...this.transferForm }).then(r => this.done(r.data.message)).catch(this.fail);
    },
    submitClose() {
      if (!this.closeForm.reason) {
        return;
      }
      if (needsNote(this.meta, 'lost_reason', this.closeForm.reason) && !this.closeForm.note.trim()) {
        this.warn('Add details for this reason.');
        return;
      }
      this.busy = true;
      leadApi('close', { lead_id: this.leadId, ...this.closeForm }).then(r => this.done(r.data.message)).catch(this.fail);
    },
    convert() {
      this.$router.push('/create-sale?lead_id=' + this.leadId);
    },
    async decideTransfer(decision: string) {
      const transfer = this.lead.pending_transfer;
      const prompt = await alertController.create({
        header: decision === 'approve' ? 'Approve transfer?' : 'Reject transfer',
        inputs: [{ name: 'note', type: 'text', placeholder: decision === 'approve' ? 'Note (optional)' : 'Reason (required)' }],
        buttons: [
          { text: 'Cancel', role: 'cancel' },
          {
            text: decision === 'approve' ? 'Approve' : 'Reject',
            handler: (input: any) => {
              leadApi('decideTransfer', { transfer_id: transfer.id, decision, note: input.note }).then(r => this.done(r.data.message)).catch(() => undefined);
            },
          },
        ],
      });
      await prompt.present();
    },
    cancelTransfer() {
      leadApi('cancelTransfer', { transfer_id: this.lead.pending_transfer.id }).then(r => this.done(r.data.message)).catch(() => undefined);
    },
  },
});
